import React, { useState, useMemo } from 'react';
import { Link, useLoaderData, useNavigate, useParams } from 'react-router-dom';
import {
  NJButton,
  NJFormItem,
  NJSelectItem,
  NJSelectRoot as NJSelectInput,
  NJSidebarDeprecated as NJSidebar,
  NJSidebarBrandDeprecated as NJSidebarBrand,
  NJSidebarDividerDeprecated as NJSidebarDivider,
  NJSidebarMenuDeprecated as NJSidebarMenu,
  NJSidebarContentDeprecated as NJSidebarContent,
  NJMultiSelectRoot,
  NJMultiSelectItem,
  NJSlider,
  NJSpinner,
  NJToastService
} from '@engie-group/fluid-design-system-react';
import { useMutation } from '@tanstack/react-query';

import { useForm, formState } from '@genai/form';
import { SidebarLink, SidebarFooter } from '@genai/sidebar';

import {
  type Assistant,
  type CreateAssistantForm,
  AssistantId
} from '../model';
import { editAssistant } from './editAssistant';
import { createAssistant } from './createAssistant';
import { type LoaderData } from '../loaders';
import styles from './page.module.css';
import { OrganizationId } from '@genai/organization';
import { CollectionId } from '@genai/collection';
import { deleteAssistant } from './deleteAssistant';
import { ConfirmationModal } from '@genai/confirmation';

const defaultMaxChunks = 5;

export const AssistantPage = (): React.ReactElement => {
  const { assistantId } = useParams() as { assistantId: AssistantId };
  const navigate = useNavigate();
  const loaderData = useLoaderData() as LoaderData;
  const [assistants, setAssistants] = useState<Assistant[]>(
    loaderData.assistants || []
  );
  const collections = useMemo(() => loaderData.collections || [], [loaderData.collections]);
  const organizations = loaderData.organizations || [];
  const listPostProcessingStrategies = loaderData.postProcessingStrategies || [];

  const currentAssistant = (loaderData.assistants || assistants).filter(
    (assistant) => assistant.uuid === assistantId
  )[0];

  const [name, setName] = useState<string>(currentAssistant?.name || '');

  const [description, setDescription] = useState<string>(
    currentAssistant?.description || ''
  );

  const [currentOrganizationUser] = useState<string>(
    loaderData.user.organization || ''
  );

  const [introduction, setIntroduction] = useState<string>(
    currentAssistant?.introduction ||
    'Bonjour je suis un assistant virtuel, comment puis-je vous aider ?'
  );
  const [disclaimer, setDisclaimer] = useState<string>(
    currentAssistant?.disclaimer ||
    'Attention : les informations que je fournis peuvent parfois être imprécises ou obsolètes. Faites preuve de vigilance et référez-vous toujours aux sources officielles et aux procédures réglementaires.'
  );
  const [systemPrompt, setSystemPrompt] = useState<string>(
    currentAssistant?.systemPrompt || ''
  );
  const [contextPrompt, setContextPrompt] = useState<string | undefined>(
    currentAssistant?.contextPrompt || undefined
  );
  const [collection, setCollection] = useState<CollectionId>(
    currentAssistant?.collection as CollectionId
  );
  const [organization, setOrganization] = useState<OrganizationId>(
    currentOrganizationUser as OrganizationId
  );
  const [postProcessingStrategies, setPostProcessingStrategies] = useState<string[]>(
    currentAssistant?.postProcessingStrategies || []);

  const [distanceMetric, setDistanceMetric] = useState<string>(
    currentAssistant?.distanceMetric ||
    (loaderData.distanceMetrics ? loaderData.distanceMetrics[0] : 'cosine')
  );
  const [chatMode, setChatMode] = useState<string>(
    currentAssistant?.ragChatMode ||
    (loaderData.ragChatModes ? loaderData.ragChatModes[0] : 'context')
  );
  const [model, setModel] = useState<string>(
    currentAssistant?.model ||
    (loaderData.models ? loaderData.models[0] : 'gpt-4o')
  );

  const [maxChunks, setMaxChunks] = useState<number>(
    currentAssistant?.maxChunks || defaultMaxChunks
  );

  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);

  const filteredCollections = useMemo(() => {
    return collections;
  }, [collections]);


  const duplicateAssistantForm = () => {
    setName((prevName) => prevName + ' copie');
    NJToastService('Assistant dupliqué avec succès !');
    setIsDuplicate(true);
  };

  const assistantToForm = (assistantId: AssistantId | null) => {
    const assistant = (loaderData.assistants || assistants).filter(
      (assistant) => assistant.uuid === assistantId
    )[0];

    setName(assistant?.name || '');
    setDescription(assistant?.description || '');
    setIntroduction(assistant?.introduction || '');
    setDisclaimer(assistant?.disclaimer || '');
    setSystemPrompt(assistant?.systemPrompt || '');
    setContextPrompt(assistant?.contextPrompt || '');
    setCollection(assistant?.collection as CollectionId);
    setOrganization(assistant?.organization as OrganizationId);
    setPostProcessingStrategies(assistant?.postProcessingStrategies || []);
    setDistanceMetric(assistant?.distanceMetric || 'cosine');
    setChatMode(assistant?.ragChatMode || 'context');
    setModel(
      assistant?.model || (loaderData.models ? loaderData.models[0] : 'gpt-4o')
    );
    setMaxChunks(assistant?.maxChunks || defaultMaxChunks);
  };

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);

  const { isPending: isEditPending, mutate: editAssistantMutate } = useMutation(
    {
      mutationFn: editAssistant.bind(this, assistantId),
      onSuccess: () => {
        NJToastService('Assistant modifié avec succès !');
      },
      onError: (error) => {
        NJToastService(`Une erreur est survenue : ${error.message}`);
      }
    }
  );

  const { isPending: isCreatePending, mutate: createAssistantMutate } =
    useMutation({
      mutationFn: createAssistant,
      onSuccess: (newAssistant) => {
        setAssistants([...(loaderData.assistants || assistants), newAssistant]);
        NJToastService('Assistant créé avec succès !');
      },
      onError: (error) => {
        NJToastService(`Une erreur est survenue : ${error.message}`);
      }
    });

  const { isPending: isDeletePending, mutate: deleteAssistantMutate } =
    useMutation({
      mutationFn: deleteAssistant.bind(null, assistantId),
      onSuccess: () => {
        setAssistants([
          ...(loaderData.assistants || assistants).filter(
            (assistant) => assistant.uuid !== assistantId
          )
        ]);
        assistantToForm(null);
        navigate('/admin/assistant');
        NJToastService('Assistant supprimé avec succès !');
      },
      onError: (error) => {
        NJToastService(`Une erreur est survenue : ${error.message}`);
      }
    });

  const isPending = isEditPending || isCreatePending || isDeletePending;

  const collectionOptions = [
    <NJSelectItem value={null as unknown as string}>
      <span className="value_null_select">----- Aucune collection -----</span>
    </NJSelectItem>,
    ...filteredCollections.map((collection) => (
      <NJSelectItem key={collection.uuid} value={collection.uuid}>
        {collection.name}
      </NJSelectItem>
    ))
  ];

  const handleCollectionChange = (newValue: string | null | undefined) => {
    const selectedCollection = collections.find(
      (collection) => collection.uuid === newValue
    );

    if (selectedCollection) {
      setCollection(newValue as CollectionId);
    }
  };

  const { formStatus, errors, onSubmit } = useForm<CreateAssistantForm>({
    handleSubmit: !assistantId || isDuplicate ? createAssistantMutate : editAssistantMutate,
    requiredFields: {
      name: 'Nom requis',
      description: 'Description requise',
      max_chunks: 'Max chunks requis',
      organization: 'Organisation requise'
    }
  });

  return (
    <>
      <NJSidebar isFolded={false}>
        <NJSidebarBrand imageSrc="/techhelp-logo.png" />
        <NJSidebarMenu className="genai-sidebar-menu">
          <div>
            <div className="genai-block_btn_new">
              <Link
                className="genai-sidebar-new"
                to={`/admin/assistant`}
              >
                <NJButton
                  className="genai-sidebar-btn-new"
                  icon="add_circle_outline"
                  label="Nouvel Assistant"
                  variant="secondary"
                />
              </Link>
            </div>
          </div>
          <NJSidebarDivider className="genai-sidebar-divider" />
          <div className="genai-sidebar-menu-content">
            <h4 className={styles['sidebar-content-title']}>
              Liste des assistants
            </h4>
            {assistants.map((assistant) => (
              <SidebarLink
                key={assistant.uuid}
                active={assistantId === assistant.uuid}
                onClick={() => assistantToForm(assistant.uuid)}
                to={`/admin/assistant/${assistant.uuid}`}
              >
                {assistant.name}
              </SidebarLink>
            ))}
          </div>
        </NJSidebarMenu>
        <SidebarFooter onNavigate={() => assistantToForm(null)} />
      </NJSidebar>
      <NJSidebarContent className={styles['sidebar-content']}>
        <div className={styles['sidebar-content-wrapper']}>
          {!assistantId || isDuplicate ? (
            <h1>Créer un nouvel assistant</h1>
          ) : (
            <h1>Modifier un assistant</h1>
          )}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit({
                name,
                description,
                introduction,
                disclaimer,
                system_prompt: systemPrompt,
                context_prompt: contextPrompt,
                collection,
                organization,
                distance_metric: distanceMetric,
                post_processing_strategies: postProcessingStrategies,
                rag_chat_mode: chatMode,
                model,
                max_chunks: maxChunks
              });
            }}
          >
            <div className={styles['sidebar-content-form-inputs-wrapper']}>
              <NJFormItem
                label="Nom de l'assistant"
                id="assistantName"
                isRequired
                value={name}
                disabled={!!assistantId && !isDuplicate}
                onChange={(event: React.ChangeEvent<HTMLElement>) => {
                  const target = event.target as HTMLInputElement;
                  setName(target.value || '');
                }}
              />

              <NJFormItem
                label="Description"
                id="description"
                isMultiline={true}
                isRequired
                value={description}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const target = event.target as HTMLInputElement;
                  setDescription(target.value || '');
                }}
              />

              <NJFormItem
                label="Introduction"
                id="introduction"
                isMultiline={true}
                value={introduction}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const target = event.target as HTMLInputElement;
                  setIntroduction(target.value || '');
                }}
              />

              <NJFormItem
                label="Avertissement"
                id="disclaimer"
                isMultiline={true}
                value={disclaimer}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const target = event.target as HTMLInputElement;
                  setDisclaimer(target.value || '');
                }}
              />

              <NJFormItem
                label="System prompt"
                id="systemPrompt"
                isMultiline={true}
                value={systemPrompt}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const target = event.target as HTMLInputElement;
                  setSystemPrompt(target.value || '');
                }}
              />

              <NJFormItem
                label="Context prompt"
                id="contextPrompt"
                isMultiline={true}
                value={contextPrompt}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const target = event.target as HTMLInputElement;
                  setContextPrompt(target.value || undefined);
                }}
              />
              <NJSelectInput
                buttonDefaultValueLabel="Choisissez une organisation"
                id="organization"
                label="Organisation"
                isDisabled={!!currentOrganizationUser}
                isRequired
                listNavigationLabel="Choisissez une organisation"
                value={currentOrganizationUser}
                onChange={(newValue) =>
                  setOrganization(newValue as OrganizationId)
                }
              >
                {organizations.map((organization) => (
                  <NJSelectItem key={organization.uuid} value={organization.uuid}>
                    {organization.name}
                  </NJSelectItem>
                ))}
              </NJSelectInput>

              <NJSelectInput
                buttonDefaultValueLabel="Choisissez une collection"
                id="collection"
                label="Collection"
                listNavigationLabel="Choisissez une collection"
                value={collection}
                onChange={handleCollectionChange}
              >
                {collectionOptions}
              </NJSelectInput>

              <NJSelectInput
                buttonDefaultValueLabel="Choisissez une distance métrique"
                id="distance_metric"
                label="Distance metric"
                listNavigationLabel="Choisissez une distance métrique"
                value={distanceMetric}
                onChange={(newValue) => setDistanceMetric(newValue as string)}
              >
                {loaderData.distanceMetrics?.map((metric) => (
                  <NJSelectItem key={crypto.randomUUID()} value={metric}>
                    {metric}
                  </NJSelectItem>
                )) || <NJSelectItem value="cosine">Cosine</NJSelectItem>}
              </NJSelectInput>

              <NJSelectInput
                buttonDefaultValueLabel="Choisissez un chat mode"
                id="rag_chat_mode"
                label="Rag chat mode"
                listNavigationLabel="Choisissez un chat mode"
                value={chatMode}
                onChange={(newValue) => setChatMode(newValue as string)}
              >
                {loaderData.ragChatModes?.map((mode) => (
                  <NJSelectItem key={crypto.randomUUID()} value={mode}>
                    {mode}
                  </NJSelectItem>
                )) || <NJSelectItem value="context">Contexte</NJSelectItem>}
              </NJSelectInput>

              <NJMultiSelectRoot
                id="post_processing_strategies"
                label="Post processing"
                value={postProcessingStrategies}
                listNavigationLabel="Utilisez les flèches Haut et Bas puis Entrée pour sélectionner une valeur"
                onChange={(newValue) => {
                  setPostProcessingStrategies(newValue);
                }}
              >

                {listPostProcessingStrategies.map((postProcessingStrategy) => (
                  <NJMultiSelectItem key={postProcessingStrategy} value={postProcessingStrategy}>
                    {postProcessingStrategy}
                  </NJMultiSelectItem>
                ))}
              </NJMultiSelectRoot>

              <NJSelectInput
                buttonDefaultValueLabel="Choisissez un modèle"
                id="model"
                label="Model"
                listNavigationLabel="Choisissez un modèle"
                value={model}
                onChange={(newValue) => setModel(newValue as string)}
              >
                {loaderData.models?.map((model) => (
                  <NJSelectItem key={crypto.randomUUID()} value={model}>
                    {model}
                  </NJSelectItem>
                )) || <NJSelectItem value="gpt-4o">GPT-4o</NJSelectItem>}
              </NJSelectInput>

              <div className={styles['sliders']}>
                <NJSlider
                  id="maxChunks"
                  label={`Max Chunks (${maxChunks})`}
                  name="maxChunks"
                  step={1}
                  value={maxChunks}
                  max={100}
                  min={1}
                  onChange={(event) => setMaxChunks(event || 0)}
                />
              </div>
            </div>

            <div className={styles['validation-container']}>
              <p>
                {formStatus === formState.error
                  ? errors.map((error) => (
                    <span key={crypto.randomUUID()}>
                        - {error} <br />
                      </span>
                  ))
                  : null}
              </p>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
              >
                {isPending && <NJSpinner scale="xs" aria-label="" />}
                {assistantId && !isDuplicate && (
                  <>
                    <ConfirmationModal
                      content="Êtes-vous sûr de vouloir supprimer l’assistant ?"
                      isOpen={isConfirmationModalOpen}
                      onClose={(confirm) => {
                        if (confirm) {
                          deleteAssistantMutate();
                        }
                        setIsConfirmationModalOpen(false);
                      }}
                    ></ConfirmationModal>
                    <NJButton
                      onClick={() => {
                        duplicateAssistantForm();
                      }}
                      variant="secondary"
                      label="Dupliquer"
                    />
                    <NJButton
                      variant="destructive"
                      label="Supprimer"
                      onClick={setIsConfirmationModalOpen.bind(null, true)}
                    ></NJButton>
                    <NJButton
                      disabled={isPending}
                      label="Discuter avec l'assistant"
                      onClick={() => navigate(`/assistant/${assistantId}/chat`)}
                    />
                  </>
                )}

                <NJButton
                  disabled={isPending}
                  type="submit"
                  label="Enregistrer l'assistant"
                />
              </div>
            </div>
          </form>
        </div>
      </NJSidebarContent>
    </>
  );
};
